import React, {useCallback, useEffect, useState} from "react";
import useIsMounted from "../../../../../hooks/use-is-mounted";
import {Row} from "reactstrap";
import Paper from "@mui/material/Paper";
import PrintCatalogueViewStepperSection from "./sections/stepper";
import PrintBrochureViewBottomActionBar from "./sections/bottom-action-bar";
import Api from "../../../../../../core/services/api/api";
import BizPrintService from "../../../../../../core/services/biz-print/biz-print-service";
import BizPrintTemplatesDialog from "../../../../../components/dialogs/biz-print-templates";
import PrintBrochureViewProductSelectionSection from "./sections/product-selection";
import PrintBrochureViewConfigureGroupsSection from "./sections/configure-groups";
import PrintBrochureViewReviewSection from "./sections/review";
import {useConfigureNotebook} from "../../../../../hooks/use-notebook-config";
import ToastService from "../../../../../../core/services/toast";
import useBehavioralAnalyticsTool from "../../../../../hooks/use-behavioural-analytics-tool";
import {stringComparator} from "../../../../../../core/services/utils/utils";

const initialBizPrintDialogState = {
    open: false,
    pageName: null,
    pageData: null,
}

export const printBrochureProductSelectionInitialState = {
    selectedIndustry: undefined,
    selectedRootCategory: undefined,
    selectedCategories: [],
    selectedProducts: [],
    selectedTags: [],
}

export const printBrochureConfigureGroupsInitialState = {
    dividerColorConfig: {
        useGroupColor: true,
        colorCode: '#000000',
    },
    groups: [],
    title: ""
}

export const printBrochureReviewInitialState = {
    dividerColorConfig: {
        useGroupColor: true,
        colorCode: '#000000',
    },
    groups: [],
    title: ""
}

/**
 * Different steps for printing a brochure.
 */
export const PrintBrochureViewSteps = {
    productSelection: 'productSelection',
    configureGroups: 'configureGroups',
    review: 'review',
}

const PrintBrochureView = () => {
    const [productSelectionState, setProductSelectionState] = useState(printBrochureProductSelectionInitialState);
    const [configureGroupsState, setConfigureGroupsState] = useState(printBrochureConfigureGroupsInitialState);
    const [reviewState, setReviewState] = useState(printBrochureReviewInitialState);
    const [step, _setStep] = useState(PrintBrochureViewSteps.productSelection);
    const [updatingStep, setUpdatingStep] = useState(false);
    const [bizPrintDialogState, setBizPrintDialogState] = useState(initialBizPrintDialogState);
    const isMounted = useIsMounted();

    const {configureNotebookTags} = useConfigureNotebook('print-brochure-view');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();

    /**
     * As soon as the component is mounted:
     * - Configures the notebook tags for the view
     * - Configures the behavioral analytics tool for the view
     */
    useEffect(() => {
        configureNotebookTags((noteTags) => noteTags.printBrochure);
        configureBehavioralTool((tags) => tags.printBrochure);
    }, [configureBehavioralTool, configureNotebookTags]);


    /**
     * Submits the product selection and updates the configure groups state.
     *
     * @type {(function(): Promise<boolean>)}
     */
    const submitProductSelection = useCallback(async () => {
        const response = await Api.getCatalogueProducts({
            categoryIds: productSelectionState.selectedCategories.map(c => c.id),
            partIds: productSelectionState.selectedProducts.map(e => e.id),
            tagIds: productSelectionState.selectedTags.map(e => e.id),
        });
        if (!isMounted())
            return false;
        if (!response.resultFlag)
            return false;
        if (!response.data?.length) {
            ToastService.warning({title: "No Products", message: "There are no products available for the selected filters."});
            return false;
        }

        const products = [...(response.data ?? [])];
        products.sort((a, b) => stringComparator(a.partNo ?? '', b.partNo ?? ''));

        const noGroupId = -1;
        const getProductGroupId = (product) => product.makeId === 0 ? noGroupId : product.makeId ?? noGroupId;
        const getProductGroupName = (product) => product?.makeTitle ?? 'N/A';

        // 1. Create Groups from Makes
        const groups = products
            .map(e => ({
                id: getProductGroupId(e),
                title: getProductGroupName(e),
                originalTitle: getProductGroupName(e),
                colorCode: null, // default color
                contrastColorCode: null, // default color
                isRemoved: false,
            }))
            .reduce((acc, curr) => {
                if (!acc.find(e => e.id === curr.id))
                    acc.push(curr);
                return acc;
            }, [])
            .map(g => ({...g, products: []})); // Initialize products array

        // 2. Map products to groups
        for (const product of products) {
            const group = groups.find(g => g.id === getProductGroupId(product));
            if (group) {
                group.products.push({
                    ...product,
                    coverImageFileName: product.coverImageFileName ? response?.configuration?.PartImageURL.concat(product.coverImageFileName ?? '') : '',
                    note: '', // Initialize note
                    isRemoved: false,
                    originalGroupId: group.id,
                    groupId: group.id,
                });
            }
        }

        setConfigureGroupsState(prevState => {
            const prevGroups = [...prevState.groups];
            const allPrevProducts = prevGroups.flatMap(g => g.products);
            const allNewProducts = groups.flatMap(g => g.products);

            // 1. Merge groups by not considering products
            const mergedGroups = groups.map(newGroup => {
                const prevGroup = prevGroups.find(g => g.id === newGroup.id);
                if (prevGroup) {
                    return {
                        ...newGroup,
                        title: prevGroup.title === prevGroup.originalTitle ? newGroup.title : prevGroup.title ?? newGroup.title,
                        originalTitle: prevGroup.title === prevGroup.originalTitle ? newGroup.originalTitle : prevGroup.originalTitle ?? newGroup.originalTitle,
                        isRemoved: prevGroup.isRemoved,
                        colorCode: prevGroup.colorCode,
                        contrastColorCode: prevGroup.contrastColorCode,
                        products: [],
                    };
                }
                // Append to the end
                return {
                    ...newGroup,
                    products: [],
                };
            });

            // 1.1 carry over the custom groups created by the user (only if their products are still present in the new selection, otherwise we consider them as removed)
            const customGroupsFromPrev = prevGroups.filter(pg => !groups.some(ng => ng.id === pg.id));
            for (const customGroup of customGroupsFromPrev) {
                const hasProductsInNewSelection = customGroup.products.some(p => allNewProducts.some(np => np.id === p.id));
                if (hasProductsInNewSelection) {
                    mergedGroups.push({
                        ...customGroup,
                        products: [], // we'll merge products in the next step, we just want to carry over the group properties for now
                    });
                }
            }

            // sort the groups by their index in prevGroups (the new ones that did not exist in prevGroups will be appended to the end
            mergedGroups.sort((a, b) => prevGroups.findIndex(pg => pg.id === a.id) - prevGroups.findIndex(pg => pg.id === b.id));

            // 2. Merge products
            for (let i = 0; i < allNewProducts.length; i++) {
                const newProduct = allNewProducts[i];
                const prevProduct = allPrevProducts.find(p => p.id === newProduct.id);

                let productToAppend = {...newProduct};

                if (prevProduct) {
                    // Carry over note and isRemoved
                    productToAppend.note = prevProduct.note;
                    productToAppend.isRemoved = prevProduct.isRemoved;

                    // 2.1 GroupId assignment
                    const targetGroupIdByPrev = prevProduct.groupId;
                    const groupExistsForPrevId = mergedGroups.some(g => g.id === targetGroupIdByPrev);

                    if (groupExistsForPrevId) {
                        productToAppend.groupId = targetGroupIdByPrev;
                    } else {
                        const targetGroupIdByOriginal = productToAppend.originalGroupId;
                        const groupExistsForOriginalId = mergedGroups.some(g => g.id === targetGroupIdByOriginal);

                        if (groupExistsForOriginalId) {
                            productToAppend.groupId = targetGroupIdByOriginal;
                        } else {
                            productToAppend.groupId = noGroupId; // special N/A group
                        }
                    }
                } else {
                    // Just attach it to its new GroupId
                    productToAppend.groupId = newProduct.groupId;
                    // Note: if the newProduct.groupId doesn't exist in mergedGroups (which shouldn't happen natively),
                    // it would fail to append. We'll fall back to {noGroupId} below just in case.
                    if (!mergedGroups.some(g => g.id === productToAppend.groupId)) {
                        productToAppend.groupId = noGroupId;
                    }
                }

                // Append the product to the resolved group
                let targetGroup = mergedGroups.find(g => g.id === productToAppend.groupId);
                if (!targetGroup) {
                    // Create the N/A group on the fly if it hasn't been created yet
                    targetGroup = {
                        id: noGroupId,
                        title: getProductGroupName(),
                        originalTitle: getProductGroupName(),
                        isRemoved: false,
                        colorCode: null,
                        contrastColorCode: null,
                        products: [],
                    };
                    mergedGroups.push(targetGroup);
                }
                targetGroup.products.push(productToAppend);
            }

            // Re-sort the products alphabetically for each group
            for (const g of mergedGroups) {
                g.products.sort((a, b) => stringComparator(a.partNo ?? '', b.partNo ?? ''));
            }

            return {
                ...prevState,
                groups: mergedGroups,
            }
        });
        return true;
    }, [isMounted, productSelectionState.selectedCategories, productSelectionState.selectedProducts, productSelectionState.selectedTags]);

    /**
     * Submits the group configuration and updates the review state.
     *
     * @type {(function(): void)}
     */
    const submitGroupConfiguration = useCallback(() => {
        const groups = configureGroupsState.groups
            .map(group => ({...group, products: group.products.filter(p => !p.isRemoved)}))
            .filter(group => !group.isRemoved && group.products.length > 0);

        if (!groups.length) {
            ToastService.warning({title: "No Groups", message: "There are no groups configured to review."});
        } else {
            setReviewState({
                dividerColorConfig: configureGroupsState.dividerColorConfig,
                groups: groups,
                title: configureGroupsState.title,
            })
        }
        return !!groups.length;
    }, [configureGroupsState.dividerColorConfig, configureGroupsState.groups, configureGroupsState.title]);

    /**
     * Submits the review and updates the print state.
     *
     * @type {(function(): void)}
     */
    const openPrintDialog = useCallback(() => {
        setBizPrintDialogState({
            open: true,
            pageName: BizPrintService.pages.productsBrochure.name,
            pageData: reviewState,
        })
    }, [reviewState]);

    /**
     * Sets the step of the view.
     *
     * 1. If the step is 'configureGroups' it will submit the product selection and if it is successful it will set the step.
     * 2. If the step is 'review' it will submit the group configuration and set the step.
     * 3. If the step is 'print' it will open the print dialog.
     * @type {(function(*): Promise<void>)}
     */
    const setStep = useCallback(async (prevStep, step) => {
        setUpdatingStep(true);
        let success = true;
        switch (step) {
            case PrintBrochureViewSteps.configureGroups:
                if (prevStep === PrintBrochureViewSteps.productSelection && !(await submitProductSelection())) {
                    success = false;
                }
                break;
            case PrintBrochureViewSteps.review:
                success = submitGroupConfiguration();
                break;
            case 'print':
                openPrintDialog();
                step = PrintBrochureViewSteps.review;
                break;
            default:
                break;
        }
        setUpdatingStep(false);
        if (success)
            _setStep(step);
    }, [openPrintDialog, submitGroupConfiguration, submitProductSelection])

    /**
     * Overrides the state of the view. This is used when the user clicks on a brochure from the list of brochures, so we need to override the state of the view with the state of the selected brochure.
     * @type {(function({configureGroupsState: *, productSelectionState: *}): void)|*}
     */
    const overrideState = useCallback(({configureGroupsState, productSelectionState}) => {
        setProductSelectionState(productSelectionState);
        setConfigureGroupsState(configureGroupsState);
        setReviewState(printBrochureReviewInitialState);
        _setStep(PrintBrochureViewSteps.productSelection);
    }, []);

    return (
        <>
            <div className={'print-catalogue-view'}>
                <Paper className={'paper d-flex flex-column justify-content-center align-items-center'}>
                    <Row className='no-gutters w-100 d-flex justify-content-between align-items-start my-4 mt-3'>
                        <h2 className={'view-title m-0 ml-3'}>
                            Print Poster
                        </h2>
                    </Row>
                    <PrintCatalogueViewStepperSection
                        step={step}
                        setStep={setStep}
                        productSelectionState={productSelectionState}
                        configureGroupsState={configureGroupsState}
                        reviewState={reviewState}
                        updatingStep={updatingStep}
                    />
                    {
                        step === PrintBrochureViewSteps.productSelection &&
                        <PrintBrochureViewProductSelectionSection
                            productSelectionState={productSelectionState}
                            setProductSelectionState={setProductSelectionState}
                            updatingStep={updatingStep}
                        />
                    }
                    {
                        step === PrintBrochureViewSteps.configureGroups &&
                        <PrintBrochureViewConfigureGroupsSection
                            configureGroupsState={configureGroupsState}
                            setConfigureGroupsState={setConfigureGroupsState}
                            updatingStep={updatingStep}
                        />
                    }
                    {
                        step === PrintBrochureViewSteps.review &&
                        <PrintBrochureViewReviewSection
                            reviewState={reviewState}
                            setReviewState={setReviewState}
                            updatingStep={updatingStep}
                        />
                    }
                </Paper>
                <PrintBrochureViewBottomActionBar
                    productSelectionState={productSelectionState}
                    configureGroupsState={configureGroupsState}
                    reviewState={reviewState}
                    step={step}
                    setStep={setStep}
                    updatingStep={updatingStep}
                    overrideState={overrideState}
                />
            </div>
            <BizPrintTemplatesDialog
                open={bizPrintDialogState.open}
                close={() => setBizPrintDialogState(initialBizPrintDialogState)}
                pageName={bizPrintDialogState.pageName}
                pageData={bizPrintDialogState.pageData}
            />
        </>
    )
}

export default PrintBrochureView;
